/**
 * Bookd - Talent Marketplace Platform
 * Copyright © 2025 Bookd. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, Smartphone } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showInstallLink, setShowInstallLink] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if PWA is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    if (!isStandalone) {
      setShowInstallLink(true);
      
      // Listen for install prompt
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      // For iOS or when prompt isn't available, show instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert('To install: Tap the Share button in Safari, then tap "Add to Home Screen"');
      }
    }
  };

  return (
    <footer className="border-t border-border/50 bg-background/95 backdrop-blur-sm mt-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src="/bookd-logo-b-only.png" 
                alt="Bookd" 
                className="h-10 w-auto"
              />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                Bookd
              </span>
            </Link>
            <p className="text-sm text-muted-foreground max-w-md">
              The marketplace connecting talented artists, athletes, and creatives with clients who need their services.
            </p>
            
            {/* Install App Link */}
            {showInstallLink && (
              <button
                onClick={handleInstallClick}
                className="mt-4 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
              >
                <Smartphone className="h-4 w-4" />
                <span>Install Bookd App</span>
                <Download className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Platform</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/browse" className="hover:text-primary transition-colors">Browse Talent</Link></li>
              <li><Link to="/venues" className="hover:text-primary transition-colors">Browse Venues</Link></li>
              <li><Link to="/subscriptions" className="hover:text-primary transition-colors">Subscriptions</Link></li>
              <li><Link to="/register" className="hover:text-primary transition-colors">Join as Talent</Link></li>
            </ul>
          </div>

          {/* Support & Legal */}
          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/support" className="hover:text-primary transition-colors">Help Center</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition-colors">Contact Us</Link></li>
              <li><Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/cancellation-policy" className="hover:text-primary transition-colors">Cancellation Policy</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground text-center md:text-left">
            © {currentYear} Bookd. All rights reserved. Unauthorized reproduction or distribution is prohibited.
          </p>
          <p className="text-xs text-muted-foreground/60">
            Protected by U.S. and international copyright laws.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
